import {preserveZadBarcode} from './productHelpers'

export const ZAD_BARCODE_LABEL_SIZE = {
  widthMm: 50,
  heightMm: 30,
} as const

export const ZAD_BARCODE_A4_COLUMNS = 3
export const ZAD_BARCODE_QTY_PRESETS = [1, 5, 10, 20] as const
export const ZAD_BARCODE_QTY_MIN = 1
export const ZAD_BARCODE_QTY_MAX = 500

export type ZadBarcodeSymbology = 'EAN13' | 'CODE128'
export type ZadBarcodeLabelFormat = 'thermal' | 'a4'

export function hasZadProductBarcode(value?: string | null): boolean {
  return preserveZadBarcode(value).trim() !== ''
}

export function isValidEan13Checksum(value: string): boolean {
  if (!/^\d{13}$/.test(value)) return false
  const digits = value.split('').map(Number)
  const sum = digits.slice(0, 12).reduce((acc, digit, index) => acc + digit * (index % 2 === 0 ? 1 : 3), 0)
  const check = (10 - (sum % 10)) % 10
  return check === digits[12]
}

export function resolveZadBarcodeSymbology(value?: string | null): ZadBarcodeSymbology {
  const barcode = preserveZadBarcode(value).trim()
  return isValidEan13Checksum(barcode) ? 'EAN13' : 'CODE128'
}

export function validateZadLabelQuantity(
  value: string | number | null | undefined
): {ok: true; value: number} | {ok: false; errorId: string} {
  if (value === undefined || value === null || value === '') {
    return {ok: false, errorId: 'ZAD.PRODUCTS.BARCODE_QTY_INVALID'}
  }
  if (typeof value === 'number') {
    if (!Number.isInteger(value)) return {ok: false, errorId: 'ZAD.PRODUCTS.BARCODE_QTY_DECIMAL'}
    if (value < ZAD_BARCODE_QTY_MIN) return {ok: false, errorId: 'ZAD.PRODUCTS.BARCODE_QTY_MIN'}
    if (value > ZAD_BARCODE_QTY_MAX) return {ok: false, errorId: 'ZAD.PRODUCTS.BARCODE_QTY_MAX'}
    return {ok: true, value}
  }
  const raw = String(value).trim()
  if (!/^\d+$/.test(raw)) {
    if (/[.,]/.test(raw)) return {ok: false, errorId: 'ZAD.PRODUCTS.BARCODE_QTY_DECIMAL'}
    return {ok: false, errorId: 'ZAD.PRODUCTS.BARCODE_QTY_INVALID'}
  }
  const numeric = Number(raw)
  if (!Number.isInteger(numeric)) return {ok: false, errorId: 'ZAD.PRODUCTS.BARCODE_QTY_DECIMAL'}
  if (numeric < ZAD_BARCODE_QTY_MIN) return {ok: false, errorId: 'ZAD.PRODUCTS.BARCODE_QTY_MIN'}
  if (numeric > ZAD_BARCODE_QTY_MAX) return {ok: false, errorId: 'ZAD.PRODUCTS.BARCODE_QTY_MAX'}
  return {ok: true, value: numeric}
}

export function buildZadBarcodeLabelCopies(quantity: number): number[] {
  const parsed = validateZadLabelQuantity(quantity)
  const count = parsed.ok ? parsed.value : 1
  return Array.from({length: count}, (_, index) => index)
}
